import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const PULSE_DELAY = 1.4;
const PULSE_DUR = 2.6;

const nodeIn = (delay) => ({
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { delay, duration: 0.9, ease: EASE },
});

function Diagram() {
    return (
        <svg
            viewBox="0 0 400 470"
            className="relative z-10 mx-auto h-auto w-full max-w-[360px] font-mono"
            role="img"
            aria-label="Handoff schematic: AI system routes exceptions through an exception gate to a human specialist"
        >
            {/* side rails */}
            <g stroke="#1E2635" strokeWidth="1">
                {[60, 100, 140, 180, 220, 260, 300, 340, 380, 420].map((y) => (
                    <line key={`l${y}`} x1="36" y1={y} x2="46" y2={y} />
                ))}
                {[60, 100, 140, 180, 220, 260, 300, 340, 380, 420].map((y) => (
                    <line key={`r${y}`} x1="354" y1={y} x2="364" y2={y} />
                ))}
            </g>

            {/* connectors */}
            <line x1="200" y1="90" x2="200" y2="196" stroke="#334155" strokeWidth="1.5" strokeDasharray="3 7" className="flow-dash" />
            <line x1="200" y1="274" x2="200" y2="362" stroke="#334155" strokeWidth="1.5" strokeDasharray="3 7" className="flow-dash" />

            {/* AI node */}
            <motion.g {...nodeIn(0.9)}>
                <rect x="130" y="26" width="140" height="64" rx="3" fill="rgba(0,229,255,0.07)" stroke="#00E5FF" strokeWidth="1.5" />
                <text x="200" y="53" textAnchor="middle" fill="#00E5FF" fontSize="14" letterSpacing="3" fontWeight="600">AI SYSTEM</text>
                <text x="200" y="74" textAnchor="middle" fill="#64748B" fontSize="8.5" letterSpacing="2">RULES · PREDICTABLE</text>
            </motion.g>

            {/* Exception gate */}
            <motion.g animate={{ opacity: [0.55, 1, 0.55] }} transition={{ duration: PULSE_DUR, repeat: Infinity, delay: PULSE_DELAY, ease: "easeInOut" }}>
                <motion.polygon
                    points="200,196 244,235 200,274 156,235"
                    fill="rgba(255,94,26,0.1)"
                    stroke="#FF5E1A"
                    strokeWidth="1.5"
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.1, duration: 0.7, ease: EASE }}
                    style={{ transformOrigin: "200px 235px" }}
                />
            </motion.g>
            <motion.g {...nodeIn(1.2)}>
                <text x="262" y="228" fill="#FF5E1A" fontSize="11" letterSpacing="2.5" fontWeight="600">EXCEPTION GATE</text>
                <text x="262" y="246" fill="#64748B" fontSize="8.5" letterSpacing="2">SOP · ESCALATION</text>
            </motion.g>

            {/* Human node */}
            <motion.g {...nodeIn(1.35)}>
                <circle cx="200" cy="410" r="48" fill="rgba(248,250,252,0.05)" stroke="#F8FAFC" strokeWidth="1.5" />
                <circle cx="200" cy="410" r="58" fill="none" stroke="rgba(248,250,252,0.15)" strokeWidth="1" strokeDasharray="2 6" />
                <text x="200" y="406" textAnchor="middle" fill="#F8FAFC" fontSize="14" letterSpacing="3" fontWeight="600">HUMAN</text>
                <text x="200" y="424" textAnchor="middle" fill="#94A3B8" fontSize="8.5" letterSpacing="2">SPECIALIST</text>
                <text x="200" y="488" textAnchor="middle" fill="#64748B" fontSize="8.5" letterSpacing="2">JUDGMENT · EXCEPTIONS</text>
            </motion.g>

            {/* travelling pulse */}
            <motion.circle
                cx="200"
                r="5"
                fill="#FF5E1A"
                initial={{ cy: 90, opacity: 0 }}
                animate={{ cy: [90, 235, 410], opacity: [0, 1, 1, 0] }}
                transition={{ duration: PULSE_DUR, times: [0, 0.5, 0.9, 1], repeat: Infinity, repeatDelay: 0.7, delay: PULSE_DELAY, ease: "easeInOut" }}
            />
        </svg>
    );
}

function HandoffSchematic() {
    return (
        <div
            data-testid="hero-handoff-schematic"
            className="relative overflow-hidden border border-line bg-panel/80 p-6 backdrop-blur-sm sm:p-8"
        >
            <img
                src="https://images.unsplash.com/photo-1631477076114-9123f721b9dc?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14]"
            />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_30%,transparent_0%,rgba(10,13,20,0.75)_100%)]" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/70 via-transparent to-ink/80" />

            <div className="relative z-10 mb-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em]">
                <span className="text-slate-400">Handoff // Schematic-01</span>
                <span className="flex items-center gap-2 text-signal">
                    <span className="h-1.5 w-1.5 animate-blink bg-signal" />
                    Live feed
                </span>
            </div>

            <Diagram />

            <div className="relative z-10 mt-4 flex items-center justify-between border-t border-line pt-4 font-mono text-[10px] uppercase tracking-[0.22em]">
                <span className="text-slate-500">Machine → gate → human</span>
                <span className="flex items-center gap-2 text-blaze">
                    <span className="h-1.5 w-1.5 animate-blink bg-blaze" />
                    Routing
                </span>
            </div>
        </div>
    );
}

const LINES = [
    [{ t: "AI HANDLES THE RULE." }],
    [{ t: "HUMANS HANDLE THE " }, { t: "EXCEPTION.", outline: true }],
    [{ t: "WE ENGINEER " }, { t: "THE HANDOFF.", blaze: true }],
];

function TiltCard({ children }) {
    const ref = useRef(null);
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 120, damping: 18 });
    const sry = useSpring(ry, { stiffness: 120, damping: 18 });

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        ry.set(((e.clientX - r.left) / r.width - 0.5) * 9);
        rx.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
    };
    const reset = () => {
        rx.set(0);
        ry.set(0);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={reset}
            style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
            className="will-change-transform"
        >
            {children}
        </motion.div>
    );
}

export default function Hero() {
    const scrollToProblem = (e) => {
        e.preventDefault();
        const el = document.getElementById("problem");
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
        else el?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section className="relative overflow-hidden pb-24 pt-32 sm:pt-36" data-testid="hero-headline-text">
            {/* blueprint grid + spotlight */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)",
                    backgroundSize: "72px 72px",
                    WebkitMaskImage:
                        "radial-gradient(75% 70% at 50% 35%, black 30%, transparent 100%)",
                    maskImage:
                        "radial-gradient(75% 70% at 50% 35%, black 30%, transparent 100%)",
                }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(55% 45% at 78% 25%, rgba(0,229,255,0.07), transparent 70%), radial-gradient(40% 35% at 12% 80%, rgba(255,94,26,0.06), transparent 70%)",
                }}
            />

            <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8">
                {/* eyebrow */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                    className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-signal"
                >
                    <span className="inline-block h-2 w-2 animate-blink bg-signal" />
                    Human-in-the-Loop Architecture
                </motion.div>

                {/* kinetic masked headline — full width */}
                <h1 className="mt-7 font-display font-extrabold uppercase leading-[0.95] tracking-tight">
                    {LINES.map((segments, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block text-[clamp(2.6rem,7.2vw,5.5rem)]"
                                initial={{ y: "115%" }}
                                animate={{ y: "0%" }}
                                transition={{ duration: 1, ease: EASE, delay: 0.25 + i * 0.13 }}
                            >
                                {segments.map((s, j) => (
                                    <span
                                        key={j}
                                        className={
                                            s.outline
                                                ? "text-outline"
                                                : s.blaze
                                                  ? "text-blaze"
                                                  : "text-white"
                                        }
                                    >
                                        {s.t}
                                    </span>
                                ))}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                {/* sub + CTA left, schematic right */}
                <div className="mt-12 grid grid-cols-1 items-center gap-12 lg:mt-16 lg:grid-cols-12 lg:gap-10">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
                            className="max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                        >
                            Scaling your business with AI requires more than just software. When
                            automated workflows hit an edge case, they break—leaving your
                            employees to clean up the mess. We design Human-in-the-Loop (HITL)
                            architecture to ensure your AI systems are efficient and your people
                            are empowered to manage the exceptions seamlessly.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1, duration: 0.9, ease: EASE }}
                            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
                        >
                            <a
                                href="https://calendly.com/tkriner/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-schedule-assessment-button"
                                className="group inline-flex items-center justify-center gap-3 bg-blaze px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-ink transition-all duration-300 hover:bg-white hover:shadow-[0_0_45px_rgba(255,94,26,0.35)]"
                            >
                                Schedule a HITL Workflow Assessment
                                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </a>
                            <a
                                href="#problem"
                                onClick={scrollToProblem}
                                className="inline-flex items-center justify-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-slate-300 transition-colors duration-300 hover:border-slate-500 hover:text-white"
                            >
                                See the problem we solve
                                <ArrowDown className="h-5 w-5" />
                            </a>
                        </motion.div>
                    </div>

                    <motion.div
                        className="lg:col-span-5"
                        initial={{ opacity: 0, y: 44 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 1.1, ease: EASE }}
                        style={{ perspective: 1200 }}
                    >
                        <TiltCard>
                            <HandoffSchematic />
                        </TiltCard>
                    </motion.div>
                </div>
            </div>

            {/* scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-5 hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500 sm:left-8 lg:flex"
            >
                Scroll
                <motion.span
                    className="block h-10 w-px bg-gradient-to-b from-blaze to-transparent"
                    animate={{ scaleY: [0.4, 1, 0.4], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    style={{ transformOrigin: "top" }}
                />
            </motion.div>
        </section>
    );
}
